import { Modal } from "react-bootstrap";
import Axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { URL_LIST_ALL } from "../configs/api";
import Pagination from "react-js-pagination";
import Spinner from "./Spinner";

export default function ModalSearchMulti(props) {
    const {
        apiname,
        alldata,
        data,
        placeholder,
        name,
        onChange,
        colvalue,
        colname,
        defaultValue,
        all,
        autoSelect,
        onDataList,
        disabled,
        multiple = false,
    } = props;

    const { user } = useSelector((state) => state.auth);

    // 👉 chỉ dùng 1 state chứa tất cả item đã chọn
    const [selectedItems, setSelectedItems] = useState([]);
    const [keySearch, setKeySearch] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState(data ? data : []);
    const [page, setPage] = useState(1);
    const [totalpage, setTotalpage] = useState(0);
    const [param, setParam] = useState(alldata ? "?" + alldata : "?");
    const [totalRecord, setTotalRecord] = useState(10);
    const searchTimeout = useRef(null);

    // Khoá so sánh theo NỘI DUNG của defaultValue. Cha thường là formik (setFieldValue tạo
    // mảng mới mỗi lần tick) nên so theo tham chiếu sẽ khiến effect chạy lại liên tục,
    // ghi đè lựa chọn và bắn thừa request lấy nhãn.
    const defaultValueKey = JSON.stringify(
        Array.isArray(defaultValue)
            ? defaultValue.map((v) => (v && typeof v === "object" ? v.value : v))
            : (defaultValue ?? "")
    );

    // Load defaultValue.
    // KHÔNG đưa `list` vào deps: mỗi lần tìm kiếm `list` đổi, effect chạy lại sẽ ép
    // selectedItems về đúng defaultValue => xoá sạch các item vừa tick nhưng chưa bấm "Chọn".
    // Việc bù nhãn cho item chưa biết label được xử lý riêng ở effect bên dưới.
    useEffect(() => {
        const raw = JSON.parse(defaultValueKey);
        // Giữ lại nhãn đã biết, tránh giá trị đang hiển thị nháy về dạng id thô.
        setSelectedItems((current) => {
            const labelOf = (v) => current.find((i) => i.value === v)?.label || String(v);
            if (multiple) {
                return Array.isArray(raw) ? raw.map((v) => ({ value: v, label: labelOf(v) })) : [];
            }
            if (raw === "" || raw === null) return [];
            return [{ value: raw, label: labelOf(raw) }];
        });
    }, [defaultValueKey, multiple]);

    // Bù nhãn khi list mới về: chỉ điền label cho item đang hiển thị giá trị thô (label === value),
    // không đụng tới danh sách đã chọn => tick của người dùng được giữ nguyên qua các lần tìm kiếm.
    useEffect(() => {
        if (!list || list.length === 0) return;
        setSelectedItems((current) => {
            let changed = false;
            const next = current.map((sel) => {
                if (sel.label && sel.label !== String(sel.value)) return sel;
                const found = list.find((i) => i.value === sel.value);
                if (!found || !found.label) return sel;
                changed = true;
                return { value: sel.value, label: found.label };
            });
            return changed ? next : current;
        });
    }, [list]);

    // Feed dữ liệu defaultValue ra input hiển thị (lấy nhãn cho các id chưa biết tên).
    useEffect(() => {
        const raw = JSON.parse(defaultValueKey);
        if (!multiple || !Array.isArray(raw) || raw.length === 0) return;

        let cancelled = false;
        let config = {
            headers: {
                Authorization: "Bearer " + user.access_token,
                asselect: JSON.stringify([colvalue + " as value", colname + " as label"]),
            },
        };
        Axios.get(
            URL_LIST_ALL + apiname + "?list_value=" + encodeURIComponent(JSON.stringify(raw)) +
            "&limit=" + Math.max(raw.length, 10),
            config
        )
            .then((res) => {
                if (cancelled || res.data?.status !== "success") return;
                const labelMap = {};
                res.data.data.forEach((i) => { labelMap[i.value] = i.label; });
                // Chỉ bổ sung nhãn, KHÔNG thay cả mảng — nếu không, response về chậm
                // sẽ xoá mất những item người dùng vừa tick trong lúc chờ.
                setSelectedItems((current) =>
                    current.map((sel) =>
                        labelMap[sel.value] ? { value: sel.value, label: labelMap[sel.value] } : sel
                    )
                );
            })
            .catch(err => console.log(err));
        return () => { cancelled = true; };
    }, [defaultValueKey, multiple, apiname, user, colvalue, colname]);

    // Chỉ gọi API lấy tất cả danh sách khi mở modal
    useEffect(() => {
        let mounted = true;
        if (!data && show) {
            let config = {
                headers: {
                    Authorization: "Bearer " + user.access_token,
                    asselect: JSON.stringify([colvalue + " as value", colname + " as label"]),
                },
            };
            setLoading(true);

            Axios.get(URL_LIST_ALL + apiname + param + "&page=" + page, config)
                .then((res) => {
                    setLoading(false);
                    if (res.data && res.data.status === "success") {
                        if (mounted) {
                            let localList = [];
                            localList.push({ value: "", label: placeholder });
                            if (all === true) localList.push({ value: "all", label: "Tất cả" });
                            localList = localList.concat(res.data.data);

                            // 🔧 merge các item đang được chọn (nếu chưa có trong list)
                            const mergedList = [...localList];
                            selectedItems.forEach(sel => {
                                if (!mergedList.find(i => i.value === sel.value)) {
                                    mergedList.push(sel);
                                }
                            });

                            setList(mergedList);
                            setTotalRecord(res.data.total);
                            setTotalpage(res.data.totalpage);

                            if (onDataList) onDataList(mergedList);
                        }
                    } else console.log(res.data.message);
                })
                .catch((err) => {
                    console.log(err);
                    setLoading(false);
                });
        }
        return () => (mounted = false);
    }, [user, apiname, data, colvalue, colname, all, alldata, param, page, show]);


    // Toggle chọn item.
    // Ở chế độ multiple, đẩy onChange ngay mỗi lần tick để state cha luôn khớp với UI —
    // nếu người dùng đóng modal bằng nút X/Đóng thì lựa chọn vẫn được giữ.
    const toggleSelect = (item) => {
        if (!multiple) {
            setSelectedItems([{ value: item.value, label: item.label }]);
            onChange(item.value);
            setShow(false);
        } else {
            const next = selectedItems.find((i) => i.value === item.value)
                ? selectedItems.filter((i) => i.value !== item.value)
                : [...selectedItems, { value: item.value, label: item.label }];
            setSelectedItems(next);
            onChange(next.map((i) => i.value));
        }
    };

    // Xoá item trong danh sách đã chọn
    const removeItem = (value) => {
        const next = selectedItems.filter((i) => i.value !== value);
        setSelectedItems(next);
        if (multiple) onChange(next.map((i) => i.value));
        else onChange("");
    };

    // Confirm khi multiple — chỉ đóng modal, giá trị đã được đẩy ra từ toggleSelect.
    const confirmSelect = () => {
        onChange(selectedItems.map((i) => i.value));
        setShow(false);
    };

    // debounce search
    const searchOnchange = (value) => {
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setParam("?&key=" + value);
            setPage(1);
        }, 500);
    };

    return (
        <div className="search-box-modal height32">
            <div className="input-group mb-3">
                <input
                    type="text"
                    className="form-control height32"
                    value={
                        multiple
                            ? selectedItems.map(i => i.label).join(", ")
                            : (selectedItems[0]?.label || "")
                    }
                    readOnly
                    placeholder={placeholder}
                />
                <input
                    type="hidden"
                    name={name}
                    value={
                        multiple
                            ? JSON.stringify(selectedItems.map(i => i.value))
                            : (selectedItems[0]?.value || "")
                    }
                />
                {!disabled && (
                    <div className="input-group-prepend">
                        <button
                            type="button"
                            className="btn btn-sm btn-success height32"
                            onClick={() => {
                                setShow(true);
                            }}
                        >
                            Chọn
                        </button>
                    </div>
                )}
            </div>

            <Modal show={show} onHide={() => setShow(false)} animation={true} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>{placeholder}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {show && (
                        <div className="w-100 list-search-box">
                            {list ? (
                                <>
                                    <div className="input-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control height32"
                                            placeholder="Tìm kiếm"
                                            value={keySearch}
                                            onChange={(e) => setKeySearch(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    searchOnchange(keySearch.trim());
                                                }
                                            }}
                                        />
                                        <div className="input-group-prepend">
                                            {keySearch !== "" && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary height32"
                                                    title="Xoá từ khoá, xem lại toàn bộ danh sách"
                                                    onClick={() => {
                                                        setKeySearch("");
                                                        searchOnchange("");
                                                    }}
                                                >
                                                    <span className="fas fa-times"></span>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-success height32"
                                                onClick={() => searchOnchange(keySearch.trim())}
                                            >
                                                <span className="fas fa-search"></span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Load list đã chọn */}
                                    <div className="mb-2">
                                        {multiple && selectedItems.length > 0 && (
                                            <div className="mb-1 d-flex justify-content-between align-items-center">
                                                <small className="text-muted">
                                                    Đã chọn <strong>{selectedItems.length}</strong> — tìm kiếm tiếp để chọn thêm, lựa chọn được giữ nguyên
                                                </small>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-danger p-0"
                                                    onClick={() => {
                                                        setSelectedItems([]);
                                                        onChange([]);
                                                    }}
                                                >
                                                    Bỏ chọn tất cả
                                                </button>
                                            </div>
                                        )}
                                        {selectedItems.map((item) => (
                                            <span
                                                key={item.value}
                                                className="btn btn-sm btn-outline-success mr-2 mb-2"
                                            >
                                                {item.label}
                                                <button
                                                    type="button"
                                                    className="btn btn-sm p-0 bg-transparent text-danger ml-1"
                                                    onClick={() => removeItem(item.value)}
                                                >
                                                    <span className="fas fa-times"></span>
                                                </button>
                                            </span>
                                        ))}
                                    </div>

                                    {loading && (
                                        <div className="w-100 d-flex justify-content-center pt-3 pb-3">
                                            <Spinner />
                                        </div>
                                    )}

                                    <ul className="w-100 list-data">
                                        {list.map((item) => {
                                            const isSelected = selectedItems.find(
                                                (i) => i.value === item.value
                                            );
                                            return (
                                                <li
                                                    key={item.value}
                                                    onClick={() => toggleSelect(item)}
                                                    className={isSelected ? "active" : ""}
                                                    style={{
                                                        cursor: "pointer",
                                                        fontWeight: isSelected ? "bold" : "normal",
                                                    }}
                                                >
                                                    {multiple && (
                                                        <input
                                                            type="checkbox"
                                                            checked={!!isSelected}
                                                            readOnly
                                                            className="mr-2"
                                                        />
                                                    )}
                                                    {item.label}
                                                </li>
                                            );
                                        })}
                                    </ul>

                                    {totalpage > 1 && (
                                        <div className="w-100 d-flex justify-content-center mt-2 pagination-group">
                                            <Pagination
                                                activePage={page}
                                                itemsCountPerPage={10}
                                                totalItemsCount={totalRecord}
                                                pageRangeDisplayed={5}
                                                itemClass="page-item"
                                                linkClass="page-link"
                                                prevPageText="<"
                                                nextPageText=">"
                                                onChange={setPage}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <h3>Không có dữ liệu</h3>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => setShow(false)}
                    >
                        Đóng
                    </button>
                    {multiple && (
                        <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={confirmSelect}
                        >
                            Xong{selectedItems.length > 0 ? " (" + selectedItems.length + ")" : ""}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
}
